import { useEffect, useMemo, useRef, useState } from 'react';
import {
  MUHURTA_DISCLAIMER,
  computeDayWindows,
  computePanchanga,
  getDefaultAdapter,
  localTimeParts,
  purposeLabel,
  suggestMuhurta,
  zonedToUtc,
  type MuhurtaPurpose,
  type MuhurtaSuggestion,
} from '@astrally/astro-engine';
import { searchCities, type CityEntry, type GeoPlace } from '@astrally/shared';
import { useApp } from '../../state/AppState';
import { InfoDot, ScreenHeader } from '../../components/common';
import { Term } from '../../components/Term';
import { localdb } from '../../lib/localdb';
import { WindowTimeline, clockAt, dateAt } from './WindowTimeline';

/**
 * Muhurta — "when should I do this?"
 *
 * The one branch of astrology that is genuinely used to make decisions rather
 * than to describe a personality, and the one Western astrology has no real
 * equivalent of. Pick what you are doing and a date range; get a ranked
 * shortlist of windows with the reason for each written out.
 *
 * THREE THINGS THIS SCREEN COMMITS TO
 *
 * 1. The reason outranks the ranking. A green tick tells you nothing you can
 *    argue with. "Abhijit muhurta, and the Moon is in Rohini, which the texts
 *    put on the shortlist for starting things" is a claim you can look up,
 *    disagree with, or take to someone who knows more than the app does. So
 *    the reasons are the body of every card and the score is a footnote.
 *
 * 2. Nothing here is generated. Every window and every reason comes out of
 *    `suggestMuhurta()` in packages/astro-engine, which is pure geometry over
 *    the real sunrise and sunset at the chosen city plus the day's five
 *    panchanga limbs. No AI call, no credits, no daily message spent — and the
 *    same inputs always produce exactly the same windows.
 *
 * 3. It says what it is. Muhurta is a living tradition consulted every day
 *    across India for weddings, purchases, journeys and launches. It is not
 *    scientifically validated and does not predict outcomes. The screen states
 *    that once, plainly, without sneering at a practice that millions of
 *    people use thoughtfully.
 */

/* --- purposes -------------------------------------------------------------- */

interface PurposeOption {
  id: MuhurtaPurpose;
  label: string;
  /** The question a reader actually arrived with. */
  blurb: string;
}

const PURPOSES: PurposeOption[] = [
  {
    id: 'launch',
    label: 'Launch something',
    blurb: 'A product, a channel, a business, a first day — anything that has a visible start.',
  },
  {
    id: 'travel',
    label: 'Travel',
    blurb: 'Setting off on a journey. Traditionally timed to the moment you leave, not to arrival.',
  },
  {
    id: 'contract',
    label: 'Sign a contract',
    blurb: 'Signing, agreeing terms, accepting an offer, registering something.',
  },
  {
    id: 'conversation',
    label: 'An important conversation',
    blurb: 'The talk you have been putting off, an apology, a proposal, a difficult ask.',
  },
  {
    id: 'purchase',
    label: 'Buy something',
    blurb: 'A purchase meant to last — a vehicle, gold, property, a serious piece of equipment.',
  },
];

const GRADE_WORD: Record<MuhurtaSuggestion['grade'], string> = {
  excellent: 'Strongly favoured',
  good: 'Favoured',
  workable: 'Workable',
};

const GRADE_BADGE: Record<MuhurtaSuggestion['grade'], string> = {
  excellent: 'badge--A',
  good: 'badge--B',
  workable: 'badge--C',
};

/* --- date helpers ---------------------------------------------------------- */

interface CivilDate {
  year: number;
  month: number;
  day: number;
}

function parseDateInput(value: string): CivilDate | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  // Round-trip through Date.UTC so 2026-02-30 is rejected rather than silently
  // becoming the 2nd of March.
  const probe = new Date(Date.UTC(year, month - 1, day));
  if (
    probe.getUTCFullYear() !== year ||
    probe.getUTCMonth() !== month - 1 ||
    probe.getUTCDate() !== day
  ) {
    return null;
  }
  return { year, month, day };
}

function addDays(d: CivilDate, n: number): CivilDate {
  const shifted = new Date(Date.UTC(d.year, d.month - 1, d.day + n));
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
  };
}

function toInputValue(d: CivilDate): string {
  return `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`;
}

/** Today where the *place* is, which is not always today where the device is. */
function todayAt(place: GeoPlace | null): CivilDate {
  const iso = place
    ? localTimeParts(Date.now(), place.lon, place.timezone).date
    : new Date().toISOString().slice(0, 10);
  return parseDateInput(iso) ?? { year: 1970, month: 1, day: 1 };
}

function isGeoPlace(value: unknown): value is GeoPlace {
  const p = value as GeoPlace | null;
  return (
    !!p &&
    typeof p.lat === 'number' &&
    typeof p.lon === 'number' &&
    typeof p.timezone === 'string' &&
    p.timezone.length > 0
  );
}

/** The longest range worth scanning. The engine caps at 92 days regardless. */
const MAX_RANGE_DAYS = 31;

/* --- screen ---------------------------------------------------------------- */

export function MuhurtaScreen() {
  const { birthProfile, ayanamsaId } = useApp();

  const [purpose, setPurpose] = useState<MuhurtaPurpose>('launch');
  const [place, setPlace] = useState<GeoPlace | null>(null);
  const [cityQuery, setCityQuery] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState<MuhurtaSuggestion[] | null>(null);
  /** The place and purpose the visible results were actually computed for. */
  const [searched, setSearched] = useState<{ place: GeoPlace; purpose: MuhurtaPurpose } | null>(null);
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  const placeRef = useRef<HTMLDivElement>(null);
  const rangeRef = useRef<HTMLDivElement>(null);
  const initialised = useRef(false);

  /* Where the day gets cut. Default to the birth city because it is the only
     place Astrally already knows the coordinates and timezone of — but say so,
     and make it changeable, because muhurta is about where you ARE. */
  useEffect(() => {
    if (initialised.current) return;
    initialised.current = true;
    let cancelled = false;
    const fallback = birthProfile?.input.place ?? null;
    localdb
      .get<GeoPlace>('kv', 'muhurtaPlace')
      .then((saved) => {
        if (cancelled) return;
        setPlace(isGeoPlace(saved) ? saved : fallback);
      })
      .catch(() => {
        if (!cancelled) setPlace(fallback);
      });
    return () => {
      cancelled = true;
    };
  }, [birthProfile]);

  /* Default range: the next week, in the place's own calendar. Filled in once
     the place is known so "today" is the reader's today. */
  useEffect(() => {
    if (!place || fromDate) return;
    const today = todayAt(place);
    setFromDate(toInputValue(today));
    setToDate(toInputValue(addDays(today, 6)));
  }, [place, fromDate]);

  const chooseCity = (city: CityEntry) => {
    const next: GeoPlace = {
      lat: city.lat,
      lon: city.lon,
      timezone: city.timezone,
      label: [city.name, city.admin, city.country].filter(Boolean).join(', '),
      countryCode: city.countryCode,
    };
    setPlace(next);
    setCityQuery('');
    setError('');
    // Results computed for another city would now be mislabelled.
    setResults(null);
    setSearched(null);
    setOpenIdx(null);
    void localdb.set('kv', 'muhurtaPlace', next);
  };

  const cityResults = useMemo(() => searchCities(cityQuery), [cityQuery]);

  /* Today's windows. Deterministic, so it renders the moment the screen opens
     — no button, no AI, no waiting. This is the part of the screen that is
     useful even to someone who never runs a search. */
  const today = useMemo(() => {
    if (!place) return null;
    try {
      const adapter = getDefaultAdapter();
      const now = new Date();
      return {
        now,
        windows: computeDayWindows(now, place.lat, place.lon, adapter, {
          timezone: place.timezone,
        }),
        panchanga: computePanchanga(now, place.lat, place.lon, adapter, ayanamsaId, {
          timezone: place.timezone,
        }),
      };
    } catch {
      return null;
    }
  }, [place, ayanamsaId]);

  /* The full day behind an expanded suggestion, computed against the place the
     search ran for rather than the current selection. */
  const openDay = useMemo(() => {
    if (openIdx === null || !results || !searched) return null;
    const s = results[openIdx];
    if (!s) return null;
    try {
      return computeDayWindows(s.startUtc, searched.place.lat, searched.place.lon, getDefaultAdapter(), {
        timezone: searched.place.timezone,
      });
    } catch {
      return null;
    }
  }, [openIdx, results, searched]);

  /**
   * The primary CTA is never disabled for an unmet requirement. Pressing it
   * with something missing names exactly what is missing and scrolls to it —
   * a dead button reads as a broken app.
   */
  const findWindows = () => {
    setError('');

    if (!place) {
      setError(
        'Pick a city first. Every muhurta window is a fraction of the real daylight where you are, so the answer changes with the place.',
      );
      placeRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      return;
    }

    const from = parseDateInput(fromDate);
    const to = parseDateInput(toDate);
    if (!from || !to) {
      setError('Choose a start date and an end date to search between.');
      rangeRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      return;
    }

    const startUtc = zonedToUtc({ ...from, hour: 0, minute: 0 }, place.timezone);
    // The range is inclusive of the end date, so it runs to the following midnight.
    const endUtc = zonedToUtc({ ...addDays(to, 1), hour: 0, minute: 0 }, place.timezone);

    if (endUtc.getTime() <= startUtc.getTime()) {
      setError('The end date is before the start date — swap them and try again.');
      rangeRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      return;
    }
    const days = Math.round((endUtc.getTime() - startUtc.getTime()) / 86_400_000);
    if (days > MAX_RANGE_DAYS) {
      setError(
        `Keep the range to ${MAX_RANGE_DAYS} days or fewer. Past that the shortlist stops being a decision and starts being a list.`,
      );
      rangeRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (endUtc.getTime() <= Date.now()) {
      setError('That range has already passed. Pick dates from today onwards.');
      rangeRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      return;
    }

    // A window that started an hour ago is not a choice. Never search the past.
    const fromUtc = new Date(Math.max(startUtc.getTime(), Date.now()));
    const searchPlace = place;
    const searchPurpose = purpose;

    setBusy(true);
    setOpenIdx(null);
    // The scan is synchronous and can take a moment on a mid-range phone; yield
    // one turn so the spinner actually paints before the main thread is taken.
    window.setTimeout(() => {
      try {
        const out = suggestMuhurta(
          searchPurpose,
          fromUtc,
          endUtc,
          searchPlace.lat,
          searchPlace.lon,
          getDefaultAdapter(),
          { timezone: searchPlace.timezone, ayanamsaId, limit: 8, maxPerDay: 2 },
        );
        setResults(out);
        setSearched({ place: searchPlace, purpose: searchPurpose });
        if (out.length === 0) {
          setError(
            'No window in this range stayed clear of Rahu Kaal, Yamaganda and Gulika for long enough to be worth offering. Widen the dates and search again.',
          );
        }
      } catch {
        setResults(null);
        setSearched(null);
        setError(
          'Astrally could not work out sunrise and sunset for that place and range. Try another city or a shorter range.',
        );
      } finally {
        setBusy(false);
      }
    }, 0);
  };

  const selected = PURPOSES.find((p) => p.id === purpose) ?? PURPOSES[0];
  const usingBirthCity =
    !!place && !!birthProfile && place.lat === birthProfile.input.place.lat && place.lon === birthProfile.input.place.lon;

  return (
    <div className="screen" data-mode="vedic">
      <ScreenHeader title="Muhurta" subtitle="When should I do this?" back />

      {/* --- what this is, said once, plainly --- */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="row" style={{ marginBottom: 8 }}>
          <span className="mode-chip">Jyotish only</span>
          <span className="tiny faint">No AI · no credits · always the same answer</span>
        </div>
        <div className="small" style={{ lineHeight: 1.6, display: 'flex', flexWrap: 'wrap' }}>
          <span>
            Muhurta
            <InfoDot term="muhurtha" />
            {' '}is the timing branch of Indian astrology. Instead of reading a birth you already
            had, you choose a good moment for something you are about to do. It is what fixes
            wedding times, shop openings, first journeys and the day people buy gold — used every
            day across India, by plenty of people who would not call themselves religious.
          </span>
        </div>
        <div className="banner banner--gold small" style={{ marginTop: 10, lineHeight: 1.55 }}>
          <b>Straight answer on what this is.</b> Muhurta is a traditional practice, not a tested
          science. There is no evidence that a time of day changes an outcome, and nothing here
          predicts one. What it does offer is a considered, consistent way to choose between times
          that already work for you — and a reason for each choice you can read and judge for
          yourself. Sort out the people, the paperwork and the flight first; use this to break the
          tie.
        </div>
        <div className="tiny faint" style={{ marginTop: 10, lineHeight: 1.55 }}>
          Western astrology has an electional branch too, but nothing on this scale: the day-by-day
          machinery below — panchanga
          <InfoDot term="panchanga" />, rahu kaal
          <InfoDot term="rahu-kaal" />, choghadiya — is specific to Jyotish, which is why this
          screen stays in Vedic mode throughout.
        </div>
      </div>

      {/* --- what are you doing? --- */}
      <div className="section-title">What are you timing?</div>
      <div className="wrap" style={{ marginBottom: 6 }}>
        {PURPOSES.map((p) => {
          const on = p.id === purpose;
          return (
            <button
              key={p.id}
              className={`chip ${on ? 'chip--on' : ''}`}
              aria-pressed={on}
              onClick={() => {
                setPurpose(p.id);
                setError('');
              }}
            >
              {on && <span aria-hidden>✓</span>}
              {p.label}
            </button>
          );
        })}
      </div>
      <div className="tiny faint" style={{ marginBottom: 14, lineHeight: 1.55 }}>
        {selected.blurb}
      </div>

      {/* --- where --- */}
      <div ref={placeRef}>
        <div className="section-title">Where will you be?</div>
        <div className="card col" style={{ gap: 10, marginBottom: 14 }}>
          <div className="tiny muted" style={{ lineHeight: 1.55 }}>
            Not where you were born — where you will actually be. Rahu kaal and the choghadiya are
            slices of the daylight between sunrise and sunset, so they land at different clock times
            in Chennai and in Chandigarh, and they move through the year.
          </div>
          <div className="spread" style={{ gap: 8 }}>
            <span className="small">
              {place ? (
                <>
                  <b>{place.label ?? `${place.lat.toFixed(2)}, ${place.lon.toFixed(2)}`}</b>
                  <span className="tiny faint"> · {place.timezone}</span>
                </>
              ) : (
                <span className="gold">No city chosen yet</span>
              )}
            </span>
          </div>
          {usingBirthCity && (
            <div className="tiny faint">
              Using your birth city because that is the one place Astrally already knows the exact
              coordinates of. Change it if you are somewhere else.
            </div>
          )}
          <label className="field">
            <span>Change city</span>
            <input
              className="input"
              value={cityQuery}
              placeholder="Search a city"
              onChange={(e) => setCityQuery(e.target.value)}
            />
          </label>
          {cityResults.length > 0 && (
            <div className="col" style={{ gap: 6 }}>
              {cityResults.map((c) => (
                <button
                  key={`${c.name}-${c.lat}`}
                  className="btn btn--ghost btn--small"
                  style={{ justifyContent: 'flex-start' }}
                  onClick={() => chooseCity(c)}
                >
                  {c.name}
                  {c.admin ? `, ${c.admin}` : ''} · {c.country}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* --- when --- */}
      <div ref={rangeRef}>
        <div className="section-title">Between which dates?</div>
        <div className="card col" style={{ gap: 10, marginBottom: 14 }}>
          <div className="row" style={{ gap: 10, alignItems: 'flex-end' }}>
            <label className="field" style={{ flex: 1, minWidth: 0 }}>
              <span>From</span>
              <input
                className="input"
                type="date"
                value={fromDate}
                min={toInputValue(todayAt(place))}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setError('');
                }}
              />
            </label>
            <label className="field" style={{ flex: 1, minWidth: 0 }}>
              <span>To</span>
              <input
                className="input"
                type="date"
                value={toDate}
                min={fromDate || toInputValue(todayAt(place))}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setError('');
                }}
              />
            </label>
          </div>
          <div className="tiny faint" style={{ lineHeight: 1.55 }}>
            A week is usually enough. Up to {MAX_RANGE_DAYS} days — a longer list is not a better
            decision. Windows that have already started today are skipped.
          </div>
        </div>
      </div>

      {error && (
        <div className="banner banner--danger small" style={{ marginBottom: 10, lineHeight: 1.5 }}>
          {error}
        </div>
      )}

      <button className="btn btn--primary btn--block" disabled={busy} onClick={findWindows}>
        {busy ? <span className="spinner" /> : `Find windows for ${selected.label.toLowerCase()}`}
      </button>

      {/* --- results --- */}
      {results && results.length > 0 && searched && (
        <>
          <div className="section-title">
            Best windows for {purposeLabel(searched.purpose)}
          </div>
          <div className="tiny faint" style={{ marginBottom: 10, lineHeight: 1.55 }}>
            Ranked, but read the reasons rather than the order. The scores compare these windows
            against each other inside this one search — they are a preference, never a probability,
            and a #4 with a reason that matches your situation beats a #1 that does not.
          </div>
          <div className="col" style={{ gap: 12 }}>
            {results.map((s, i) => (
              <SuggestionCard
                key={`${s.startUtc.getTime()}-${i}`}
                rank={i + 1}
                suggestion={s}
                place={searched.place}
                open={openIdx === i}
                onToggle={() => setOpenIdx(openIdx === i ? null : i)}
                dayWindows={openIdx === i ? openDay : null}
              />
            ))}
          </div>
          <div className="banner banner--info small" style={{ marginTop: 14, lineHeight: 1.55 }}>
            {MUHURTA_DISCLAIMER}
          </div>
        </>
      )}

      {/* --- today, always, with no button pressed --- */}
      <div className="section-title">Today at a glance</div>
      {today ? (
        <>
          <div className="tiny faint" style={{ marginBottom: 10, lineHeight: 1.55 }}>
            The same maths, applied to right now, whether or not you searched for anything. This is
            the view most people actually want: which parts of today the tradition says to sit out.
          </div>
          <WindowTimeline
            windows={today.windows}
            lon={place?.lon ?? 0}
            timezone={place?.timezone}
            placeLabel={place?.label}
            nowUtc={today.now}
            panchanga={today.panchanga}
            defaultShowBands={false}
          />
        </>
      ) : (
        <div className="card small muted">
          {place
            ? 'Astrally could not work out sunrise and sunset here today — that happens inside the polar circles, where the Sun may not rise or set at all.'
            : 'Choose a city above and today’s windows appear here immediately.'}
        </div>
      )}

      {/* --- how it is worked out --- */}
      <div className="section-title">How these windows are worked out</div>
      <div className="card small muted" style={{ lineHeight: 1.6 }}>
        <p style={{ marginBottom: 8 }}>
          None of this is a guess or a generated opinion. Astrally takes the real sunrise and sunset
          at your city, splits the daylight into eight equal parts, and applies the classical tables
          every Indian almanac prints: <b>rahu kaal</b>
          <InfoDot term="rahu-kaal" />, yamaganda and gulika each take a fixed eighth chosen by the
          weekday, <b>abhijit</b> is the middle stretch of the day around local noon, and{' '}
          <b>choghadiya</b> names all sixteen parts of day and night.
        </p>
        <p style={{ marginBottom: 8 }}>
          The inauspicious eighths are then cut out of the favourable ones, and whatever survives is
          scored against the five limbs of the panchanga
          <InfoDot term="panchanga" /> at that moment — the tithi
          <InfoDot term="tithi" /> (lunar day), the vaara
          <InfoDot term="vaara" /> (weekday and its ruling graha), the nakshatra
          <InfoDot term="nakshatra" /> that <Term id="graha.chandra">Chandra</Term> is sitting in,
          plus the yoga and karana. Each
          purpose weighs those differently, which is why a good window for travel is not
          automatically a good one for signing.
        </p>
        <p>
          Every reason on a card names the rule it came from, so you can check it against any
          panchanga or ask an astrologer whether they agree. Where a rule counted against a window,
          it is printed too, not hidden.
        </p>
      </div>
    </div>
  );
}

/* --- one suggestion -------------------------------------------------------- */

function SuggestionCard({
  rank,
  suggestion: s,
  place,
  open,
  onToggle,
  dayWindows,
}: {
  rank: number;
  suggestion: MuhurtaSuggestion;
  place: GeoPlace;
  open: boolean;
  onToggle: () => void;
  dayWindows: ReturnType<typeof computeDayWindows> | null;
}) {
  const hours = Math.floor(s.durationMinutes / 60);
  const mins = s.durationMinutes % 60;
  const duration = hours === 0 ? `${mins} min` : mins === 0 ? `${hours} h` : `${hours} h ${mins} m`;

  return (
    <div className="card" data-mode="vedic">
      <div className="spread" style={{ alignItems: 'flex-start', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div className="h2">
            {clockAt(s.startUtc.getTime(), place.lon, place.timezone)} –{' '}
            {clockAt(s.endUtc.getTime(), place.lon, place.timezone)}
          </div>
          <div className="small muted" style={{ marginTop: 2 }}>
            {s.vaara}, {dateAt(s.startUtc.getTime(), place.lon, place.timezone)} · {duration}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <span className={`badge ${GRADE_BADGE[s.grade]}`}>#{rank}</span>
          <div className="tiny" style={{ marginTop: 4, fontWeight: 600 }}>
            {GRADE_WORD[s.grade]}
          </div>
        </div>
      </div>

      {/* The traceable facts this window was scored from. */}
      <div className="wrap" style={{ gap: 6, marginTop: 10 }}>
        {s.isAbhijit && (
          <span className="chip chip--fact" style={{ cursor: 'default' }}>
            Abhijit muhurta
          </span>
        )}
        {s.isBrahmaMuhurta && (
          <span className="chip chip--fact" style={{ cursor: 'default' }}>
            Brahma muhurta
          </span>
        )}
        {s.choghadiya && (
          <span className="chip chip--fact" style={{ cursor: 'default' }}>
            {s.choghadiya} choghadiya
          </span>
        )}
        <span
          className="chip chip--fact"
          style={{ cursor: 'default', display: 'inline-flex', alignItems: 'center' }}
        >
          {s.limbs.tithiName}
          <InfoDot term="tithi" />
        </span>
        <span
          className="chip chip--fact"
          style={{ cursor: 'default', display: 'inline-flex', alignItems: 'center' }}
        >
          Chandra in {s.limbs.nakshatraName}
          <InfoDot term="nakshatra" />
        </span>
        <span
          className="chip chip--fact"
          style={{ cursor: 'default', display: 'inline-flex', alignItems: 'center' }}
        >
          {s.vaara}
          <InfoDot term="vaara" />
        </span>
      </div>

      {/* The point of the whole feature. */}
      <div className="section-title" style={{ marginBottom: 6 }}>
        Why this window
      </div>
      <div className="col" style={{ gap: 8 }}>
        {s.reasons.map((reason, i) => (
          <div key={i} className="row" style={{ alignItems: 'flex-start', gap: 8 }}>
            <span aria-hidden style={{ color: 'var(--ok)', lineHeight: 1.6 }}>
              +
            </span>
            <span className="small" style={{ lineHeight: 1.6 }}>
              {reason}
            </span>
          </div>
        ))}
      </div>

      <div className="section-title" style={{ marginBottom: 6 }}>
        What is imperfect about it
      </div>
      {s.cautions.length > 0 ? (
        <div className="col" style={{ gap: 8 }}>
          {s.cautions.map((caution, i) => (
            <div key={i} className="row" style={{ alignItems: 'flex-start', gap: 8 }}>
              <span aria-hidden style={{ color: 'var(--danger)', lineHeight: 1.6 }}>
                −
              </span>
              <span className="small muted" style={{ lineHeight: 1.6 }}>
                {caution}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="small muted" style={{ lineHeight: 1.6 }}>
          Nothing in the scan counted against this one — which means no rule was broken, not that
          the day is guaranteed to go well.
        </div>
      )}

      <div className="divider" />
      <div className="spread" style={{ gap: 10 }}>
        <span className="tiny faint" style={{ lineHeight: 1.5 }}>
          Score {s.score}/100 — how it compares with the other windows in this search only.
        </span>
        <button className="btn btn--ghost btn--small" aria-expanded={open} onClick={onToggle}>
          {open ? 'Hide the day' : 'See the whole day'}
        </button>
      </div>

      {open &&
        (dayWindows ? (
          <div style={{ marginTop: 12 }}>
            <WindowTimeline
              windows={dayWindows}
              lon={place.lon}
              timezone={place.timezone}
              placeLabel={place.label}
              highlight={{ startUtc: s.startUtc, endUtc: s.endUtc, label: 'This window' }}
              defaultShowBands={false}
            />
          </div>
        ) : (
          <div className="small muted" style={{ marginTop: 10 }}>
            The full day for this date could not be worked out.
          </div>
        ))}
    </div>
  );
}
